import DocumentViewerModal from "components/DocumentViewer";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { editAppealLitigation } from "services/prosecutorDecipilineService";
import { CouncilLitigation, Litigation } from "types";
import { ROLES } from "utils/roles";
import AddLitigationFeedbackModal from "./AddLitigationFeedbackModal";

interface LitigationProps {
  fromCourt?: Boolean;
  litigationData: Litigation[];
  councilLitigationData: CouncilLitigation[] | [];
  onLitigationRefresh: any;
  showForHead?: boolean;
}

const calculate_remaining_days = ({ date }: { date: string }) => {
  const appeal = new Date(date ?? "");
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);

  return remainingDays;
};

export default function AppealLitigationCard({
  fromCourt = false,
  litigationData,
  councilLitigationData,
  onLitigationRefresh,
  showForHead = false,
}: LitigationProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const canViewDailyHearingProsecutor = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
  ]);

  const canAddLitigationFeedback = useHasRole([
    ROLES.INSPECTION_DIRECTOR,
    ROLES.INSPECTION_COORDINATOR,
  ]);

  const isRegionalCouncilHead = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedLitigation, setSelectedLitigation] = useState<any>(null);
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState({
    hearing_date: "",
    next_appointment_date: "",
  });
  const [isDateUpdating, setIsDateUpdating] = useState(false);
  // const [litigationFeedbacks, setLitigationFeedbacks] = useState<any>([]);

  // const [error, setError] = useState("");
  // const [loading, setLoading] = useState(true);

  // const loadFeedback = useCallback(async () => {
  //   if (!litigationData) return;

  //   setLoading(true);
  //   try {
  //     const resData = await fetchAppealLitigationFeedbacks();
  //     if (resData && resData.length > 0) {
  //       // setLitigationFeedbacks(resData);
  //     }
  //   } catch (err) {
  //     setError("Failed to feedback.");
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [litigationData]);

  // useEffect(() => {
  //   loadFeedback();
  // }, [litigationData]);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startDateEdit = (item: Litigation) => {
    setEditingDateId(item.id ?? null);
    setDateDraft({
      hearing_date: toDateInputValue(item.hearing_date as string),
      next_appointment_date: toDateInputValue(
        item.next_appointment_date as string,
      ),
    });
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setDateDraft({ hearing_date: "", next_appointment_date: "" });
  };

  const saveDateEdit = async (item: Litigation) => {
    if (!item?.id) {
      showToast("Litigation id not found", "error");
      return;
    }

    const currentHearingDate = toDateInputValue(item.hearing_date as string);
    const currentNextDate = toDateInputValue(
      item.next_appointment_date as string,
    );

    const payload: Record<string, any> = {};

    if (
      dateDraft.hearing_date &&
      dateDraft.hearing_date !== currentHearingDate
    ) {
      payload.hearing_date = dateDraft.hearing_date;
    }

    if (
      dateDraft.next_appointment_date &&
      dateDraft.next_appointment_date !== currentNextDate
    ) {
      payload.next_appointment_date = dateDraft.next_appointment_date;
    }

    if (!payload.hearing_date && !payload.next_appointment_date) {
      showToast("No date change detected", "error");
      return;
    }

    try {
      setIsDateUpdating(true);
      await editAppealLitigation(payload as any, item.id, false);
      await Promise.resolve(onLitigationRefresh?.());
      cancelDateEdit();
      showToast("Date updated", "success");
    } catch (err) {
      showToast("Failed to update date", "error");
    } finally {
      setIsDateUpdating(false);
    }
  };

  return (
    <div className="mb-8">
      <div className="space-y-4">
        {(canViewDailyHearingProsecutor || showForHead) &&
          [...(litigationData || [])]
            ?.filter((a) =>
              showForHead && isRegionalCouncilHead
                ? Array.isArray(a?.documents) && a?.documents?.length > 0
                : true,
            )
            ?.sort((a, b) => {
              const aHasDecision = !!a.council_decision_summary;
              const bHasDecision = !!b.council_decision_summary;

              // If only one has a decision, show it first
              if (aHasDecision && !bHasDecision) return -1;
              if (!aHasDecision && bHasDecision) return 1;

              // Otherwise, sort by next appointment date descending
              const dateA = new Date(a?.next_appointment_date ?? "").getTime();
              const dateB = new Date(b?.next_appointment_date ?? "").getTime();

              return dateA - dateB;
            })

            ?.map((obj: any) => (
              <div
                key={obj.id}
                className={
                  obj?.council_decision_summary
                    ? "shadow-md rounded-xl p-4 w-full mx-auto bg-white border-l-8 border-black"
                    : "shadow-md rounded-xl p-4 w-full mx-auto"
                }
              >
                {/* Header */}
                {obj?.assigned_prosecutor && (
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={require("../../../images/avatar.png")}
                      alt="profile"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <h2 className="text-sm font-semibold">
                        {obj?.assigned_prosecutor || "-"}
                      </h2>
                      <p className="text-xs text-gray-500">
                        {t("assignedProsecutor")}
                      </p>
                    </div>
                  </div>
                )}

                {obj?.hearing_date && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">{t("hearingDate")}</p>
                      <div className="flex items-center gap-3">
                        <p className="block text-black font-medium">
                          {obj?.hearing_date || "-"}
                        </p>
                        <button
                          type="button"
                          onClick={() => startDateEdit(obj)}
                          className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                        >
                          {t("updateDate")}
                        </button>
                      </div>
                    </div>
                    <hr />
                  </>
                )}

                {obj?.next_appointment_date && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">
                        {t("nextAppointmentDate")}
                      </p>
                      <p className="block text-black font-medium">
                        {obj?.next_appointment_date || "-"}
                      </p>
                    </div>
                    <hr />
                  </>
                )}

                {editingDateId === obj.id && (
                  <div className="my-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("hearingDate")}
                        </label>
                        <input
                          type="date"
                          value={dateDraft.hearing_date}
                          onChange={(e) =>
                            setDateDraft((prev) => ({
                              ...prev,
                              hearing_date: e.target.value,
                            }))
                          }
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t("nextAppointmentDate")}
                        </label>
                        <input
                          type="date"
                          value={dateDraft.next_appointment_date}
                          onChange={(e) =>
                            setDateDraft((prev) => ({
                              ...prev,
                              next_appointment_date: e.target.value,
                            }))
                          }
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isDateUpdating}
                        onClick={() => saveDateEdit(obj)}
                        className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                      >
                        {isDateUpdating ? "Updating..." : "Save Date"}
                      </button>
                      <button
                        type="button"
                        disabled={isDateUpdating}
                        onClick={cancelDateEdit}
                        className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {obj?.given_order && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">
                        {fromCourt ? t("courtOrder") : t("councilOrder")}
                      </p>
                      <p className="block text-black font-medium">
                        {obj?.given_order || "-"}
                      </p>
                    </div>

                    <hr />
                  </>
                )}

                {obj?.prosecutor_opinion_on_decision_appeal && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">
                        {t("prosecutorOpinion")}
                      </p>
                      <p className="block text-black font-medium">
                        {obj?.prosecutor_opinion_on_decision_appeal || "-"}
                      </p>
                    </div>

                    <hr />
                  </>
                )}

                {obj?.council_decision_day && (
                  <>
                    <div className="w-full flex flex-wrap items-center justify-between gap-4 my-2">
                      <p className="block text-gray-600">
                        {t("remainingDaysForAppeal")}
                      </p>
                      {(() => {
                        const days = calculate_remaining_days({
                          date:
                            councilLitigationData[0]?.council_decision_day ??
                            "",
                        });
                        const isExpired = days < 0;
                        const absDays = Math.abs(days);
                        const dayLabel = absDays === 1 ? t("day") : t("days");

                        if (!councilLitigationData[0]?.council_decision_day)
                          return <>-</>;

                        return (
                          <p
                            className={`block font-medium ${
                              isExpired ? "text-red-600" : "text-green-700"
                            }`}
                          >
                            {isExpired
                              ? `${t("expired")} ${absDays} ${dayLabel} ${t("ago")}`
                              : `${days} ${dayLabel}`}
                          </p>
                        );
                      })()}
                    </div>

                    <hr />
                  </>
                )}

                {/* Info Rows */}
                {obj?.daily_report_summary && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("dailyReportSummary")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.daily_report_summary || "-"}
                    </p>
                  </div>
                )}

                {obj?.appointment_reason && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("appointmentReason")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.appointment_reason || "-"}
                    </p>
                  </div>
                )}

                {obj?.prosecutor_opinion_on_decision && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("prosecutorOpinionOnDecision")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.prosecutor_opinion_on_decision || "-"}
                    </p>
                  </div>
                )}

                {obj?.director_feedbacks_appeal?.map((feedback: any) => (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("feedback")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {feedback?.comment || "-"}
                    </p>
                  </div>
                ))}

                {obj?.council_decision_summary && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {fromCourt
                        ? t("decisionSummary")
                        : t("councilDecisionSummary")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.council_decision_summary || "-"}
                    </p>
                  </div>
                )}

                {obj?.council_decision_type && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {fromCourt
                        ? t("courtDecisionType")
                        : t("councilDecisionType")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.council_decision_type || "-"}
                    </p>
                  </div>
                )}

                {obj?.discipline_measure_type && (
                  <div className="my-3 mb-5">
                    <p className="text-[#002855] font-medium">
                      {t("decisionMeasureType")}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {obj?.discipline_measure_type || "-"}
                    </p>
                  </div>
                )}

                {/* {sconsole.log(obj?.documents)} */}
                <DocumentViewerModal
                  documents={(obj?.documents || []) as any}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
                />

                {canAddLitigationFeedback &&
                  obj?.hearing_date &&
                  obj?.director_feedbacks_appeal &&
                  obj.director_feedbacks_appeal.length <= 0 && (
                    <button
                      className="w-full bg-[#5f5f5f] hover:bg-gray-800 border border-white text-white py-2 rounded-lg mt-2"
                      onClick={() => {
                        setSelectedLitigation(obj);
                        setIsFeedbackOpen(true);
                      }}
                    >
                      {t("addFeedback")}
                    </button>
                  )}
              </div>
            ))}
      </div>

      {litigationData && (
        <AddLitigationFeedbackModal
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
          litigationData={selectedLitigation}
          onRefresh={onLitigationRefresh}
        />
      )}
    </div>
  );
}
